/* eslint-disable no-inline-style */
import _ from 'lodash';
import BigNumber from 'bignumber.js';
import { fetchGoThumbSignedUrl } from 'utilsModule/gothumb';
import { enGB, vi, zhCN, id, th, ms as msLocale } from 'date-fns/locale';
import { format } from 'date-fns';

/**
 * This function changes the given string to title case
 *
 * '_' will be replaced with space
 *
 * @param {string} str - String to change to title case
 */
const toTitleCase = (str) => {
  if (typeof str !== 'string') return '';
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/(?: |\b)(\w)/g, (key) => key.toUpperCase());
};

/**
 * This function masks the given string
 *
 * Given string must contain only one '@'.
 *
 * @param {string} email - Email String
 */
const maskEmail = (email) => {
  const split = email.split('@');
  return `${email.substr(0, 4)}${new Array(split[0].length - 4).fill('*').join('')}@${split[1]}`;
};

/**
 * This function truncates the given string by the given number of characters using an ellipsis
 *
 * @param {string} str - String to be truncated
 * @param {number} [noOfChars] - Maximum number of chars to display including the ellipsis [3 chars] (default 40)
 */
const truncateWithEllipsis = (str, noOfChars = 40) => {
  const options = {
    length: noOfChars,
    omission: '...',
  };
  return _.truncate(str, options);
};

/**
 * This function renames the web/tab title using React's document.title overwrite
 *
 * @param {string} [role] - User Role String
 * @param {string} [orgType] - User Org String
 */
const renameDocumentTitle = (role, orgType) => {
  let title = 'HealthBeats®';

  if (role === 'ADMIN') title += ` - ${toTitleCase(orgType)}`;
  if (role) title += ` - ${toTitleCase(role)}`;

  document.title = title;
};

const inputNumberLimit = (event, maxNumberBelow, maxDecimal) => {
  const {
    target: { value },
  } = event;
  const reg = new RegExp(`^\\d+(\\.\\d{0,${maxDecimal}})?$`);
  if ((reg.test(value) && value < maxNumberBelow) || !value) {
    return true;
  }
  event.preventDefault();
};

// To fix imprecise calculations due to 64 bit systems as documented at
// https://javascript.info/number#imprecise-calculations
export function roundFloatToFixedDecimal(numValue, numDecimals) {
  // Return NaN if value is null or undefined
  if (numValue == null || numValue == undefined) return NaN;
  // Return value untouched if number of decimals is undefined or null
  if (isNaN(numDecimals) || numDecimals == null || numDecimals == undefined) {
    return numValue;
  } else {
    const x = new BigNumber(numValue);
    return x.decimalPlaces(parseInt(numDecimals)).toNumber();
  }
}

export const listOptions = (t) => {
  // TO SLOWLY MIGRATE ALL THE KEYS TO BE TRANSLATED
  if (t) {
    return {
      az: [
        { value: 'ASC', label: t('common:sortingListOptions.name.AZ') },
        { value: 'DESC', label: t('common:sortingListOptions.name.ZA') },
      ],
      ascDesc: [
        { value: 'ASC', label: t('common:sortingListOptions.ascDesc.ascending') },
        { value: 'DESC', label: t('common:sortingListOptions.ascDesc.descending') },
      ],
      oldNew: [
        { value: 'DESC', label: t('common:sortingListOptions.oldNew.newestToOldest') },
        { value: 'ASC', label: t('common:sortingListOptions.oldNew.oldestToNewest') },
      ],
      chargeCodeTypeSorting: [
        { value: 'base', label: t('common:sortingListOptions.chargeCodeTypeSorting.base') },
        { value: 'addon_after', label: t('common:sortingListOptions.chargeCodeTypeSorting.addOnAfter') },
        { value: 'addon_during', label: t('common:sortingListOptions.chargeCodeTypeSorting.addOnDuring') },
        { value: 'consumables', label: t('common:sortingListOptions.chargeCodeTypeSorting.consumables') },
        { value: 'kiosk', label: t('common:sortingListOptions.chargeCodeTypeSorting.kiosk') },
      ],
      alertCategory: [
        { value: 'ALL_ALERTS', label: t('alerts.filteredTitleDisplay.allAlerts') },
        { value: 'SPOT_ALERTS', label: t('alerts.filteredTitleDisplay.spotAlerts') },
        { value: 'LIVE_ALERTS', label: t('alerts.filteredTitleDisplay.liveContinuousAlerts') },
        { value: 'QUESTIONNAIRE_ALERTS', label: t('alerts.filteredTitleDisplay.questionnaireAlerts') },
      ],
      alertType: [
        { value: 'CRITICALLY_LOW', label: t('common:sortingListOptions.alertType.criticallyLowFirst') },
        { value: 'LOW', label: t('common:sortingListOptions.alertType.lowFirst') },
        { value: 'CRITICALLY_HIGH', label: t('common:sortingListOptions.alertType.criticallyHighFirst') },
        { value: 'HIGH', label: t('common:sortingListOptions.alertType.highFirst') },
        { value: 'MISSED', label: t('common:sortingListOptions.alertType.missedFirst') },
      ],
      statusAlert: [
        { value: 'NEW', label: t('common:sortingListOptions.statusAlert.newFirst') },
        { value: 'REVIEWED', label: t('common:sortingListOptions.statusAlert.reviewedFirst') },
        { value: 'CLOSED', label: t('common:sortingListOptions.statusAlert.closedFirst') },
      ],
      price: [
        { value: 'ASC', label: t('common:sortingListOptions.price.lowToHigh') },
        { value: 'DESC', label: t('common:sortingListOptions.price.highToLow') },
      ],
      kioskStatus: [
        { value: 'available', label: t('common:sortingListOptions.kioskStatus.availableFirst') },
        { value: 'in_use', label: t('common:sortingListOptions.kioskStatus.inUseFirst') },
      ],
      expiredStatus: [
        { value: 'INACTIVE', label: t('common:sortingListOptions.expiredStatus.inactiveFirst') },
        { value: 'ACTIVE', label: t('common:sortingListOptions.expiredStatus.activeFirst') },
        { value: 'EXPIRED', label: t('common:sortingListOptions.expiredStatus.expiredFirst') },
      ],
      shortLong: [
        { value: 'ASC', label: t('common:sortingListOptions.shortLong.shortestToLongest') },
        { value: 'DESC', label: t('common:sortingListOptions.shortLong.longestToShortest') },
      ],
      chargeCodeType: [
        { value: 'base', label: t('common:sortingListOptions.chargeCodeType.base') },
        { value: 'addon_after', label: t('common:sortingListOptions.chargeCodeType.addOnAfter') },
        { value: 'addon_during', label: t('common:sortingListOptions.chargeCodeType.addOnDuring') },
        { value: 'kiosk', label: t('common:sortingListOptions.chargeCodeType.kiosk') },
      ],
      gender: [
        { value: 'FEMALE', label: t('common:sortingListOptions.gender.femaleFirst') },
        { value: 'MALE', label: t('common:sortingListOptions.gender.maleFirst') },
        { value: 'OTHERS', label: t('common:sortingListOptions.gender.otherFirst') },
        { value: 'UNKNOWN', label: t('common:sortingListOptions.gender.unknownFirst') },
        { value: 'UNSPECIFIED', label: t('common:sortingListOptions.gender.unspecifiedFirst') },
      ],
      enrolledStatus: [
        { value: 'ENROLLED', label: t('common:sortingListOptions.enrolledStatus.enrolledFirst') },
        { value: 'NOT_ENROLLED', label: t('common:sortingListOptions.enrolledStatus.notEnrolledFirst') },
        // { value: 'DISCHARGED', label: 'Discharged first' }
      ],
      lastSync: [
        { value: 'ASC', label: t('common:sortingListOptions.lastSync.ascending') },
        { value: 'DESC', label: t('common:sortingListOptions.lastSync.descending') },
      ],
      admissionType: [
        { value: 'hospAdmission', label: t('common:sortingListOptions.admissionType.hospitalAdmissionFirst') },
        { value: 'emerVisit', label: t('common:sortingListOptions.admissionType.emergencyVisitFirst') },
      ],
      priority: [
        { value: 'high', label: t('common:sortingListOptions.priority.highFirst') },
        { value: 'medium', label: t('common:sortingListOptions.priority.mediumFirst') },
        { value: 'low', label: t('common:sortingListOptions.priority.lowFirst') },
      ],
      status: [
        { value: 'active', label: t('common:sortingListOptions.status.activeFirst') },
        { value: 'resolved', label: t('common:sortingListOptions.status.resolvedFirst') },
        { value: 'not_applicable', label: t('common:sortingListOptions.status.notApplicableFirst') },
      ],
    };
  }

  return {
    az: [
      { value: 'ASC', label: 'A to Z' },
      { value: 'DESC', label: 'Z to A' },
    ],
    ascDesc: [
      { value: 'ASC', label: 'Ascending' },
      { value: 'DESC', label: 'Descending' },
    ],
    gender: [
      { value: 'FEMALE', label: 'Female first' },
      { value: 'MALE', label: 'Male first' },
      { value: 'OTHERS', label: 'Other first' },
      { value: 'UNKNOWN', label: 'Unknown first' },
      { value: 'UNSPECIFIED', label: 'Unspecified first' },
    ],
    shortLong: [
      { value: 'ASC', label: 'Shortest to Longest' },
      { value: 'DESC', label: 'Longest to Shortest' },
    ],
    enrolledStatus: [
      { value: 'ENROLLED', label: 'Enrolled first' },
      { value: 'NOT_ENROLLED', label: 'Not Enrolled first' },
      // { value: 'DISCHARGED', label: 'Discharged first' }
    ],
    oldNew: [
      { value: 'DESC', label: 'Newest to Oldest' },
      { value: 'ASC', label: 'Oldest to Newest' },
    ],
    alertType: [
      { value: 'CRITICALLY_LOW', label: 'Critically Low first' },
      { value: 'LOW', label: 'Low first' },
      { value: 'CRITICALLY_HIGH', label: 'Critically High first' },
      { value: 'HIGH', label: 'High first' },
      { value: 'MISSED', label: 'Missed first' },
    ],
    statusAlert: [
      { value: 'NEW', label: 'New first' },
      { value: 'REVIEWED', label: 'Reviewed first' },
      { value: 'CLOSED', label: 'Closed first' },
    ],
    lastSync: [
      { value: 'ASC', label: 'Ascending' },
      { value: 'DESC', label: 'Descending' },
    ],
    chargeCodeTypeSorting: [
      { value: 'base', label: 'Base Subscription first' },
      { value: 'addon_after', label: 'Add-On (After) first' },
      { value: 'addon_during', label: 'Add-On (During) first' },
      { value: 'consumables', label: 'Consumables first' },
      { value: 'kiosk', label: 'Kiosk first' },
    ],
    price: [
      { value: 'ASC', label: 'Low to High' },
      { value: 'DESC', label: 'High to Low' },
    ],
    chargeCodeType: [
      { value: 'base', label: 'Base Subscription' },
      { value: 'addon_after', label: 'Add-On (After)' },
      { value: 'addon_during', label: 'Add-On (During)' },
      { value: 'kiosk', label: 'Kiosk Subscription' },
    ],
    alertCategory: [
      { value: 'ALL_ALERTS', label: 'All Alerts' },
      { value: 'SPOT_ALERTS', label: 'Spot Alerts' },
      { value: 'LIVE_ALERTS', label: 'LIVE Continuous Alerts' },
      { value: 'QUESTIONNAIRE_ALERTS', label: 'Questionnaire Alerts' },
    ],
    priority: [
      { value: 'high', label: 'High First' },
      { value: 'medium', label: 'Medium First' },
      { value: 'low', label: 'Low First' },
    ],
    status: [
      { value: 'active', label: 'Active First' },
      { value: 'resolved', label: 'Resolved First' },
      { value: 'not_applicable', label: 'N.A First' },
    ],
    admissionType: [
      { value: 'hospAdmission', label: 'Hospital Admission First' },
      { value: 'emerVisit', label: 'Emergency Visit First' },
    ],
    kioskStatus: [
      { value: 'available', label: 'Available First' },
      { value: 'in_use', label: 'In Use First' },
    ],
    expiredStatus: [
      { value: 'INACTIVE', label: 'Inactive First' },
      { value: 'ACTIVE', label: 'Active First' },
      { value: 'EXPIRED', label: 'Expired First' },
    ],
  };
};

const listSortedDuration = (subscriptionPeriodType) => {
  if (subscriptionPeriodType === 'DAY') return 1;
  if (subscriptionPeriodType === 'WEEK') return 7;
  if (subscriptionPeriodType === 'MONTH') return 30;
  if (subscriptionPeriodType === 'YEAR') return 365;
};

const participantListFilterOptions = (t) => [
  {
    columnTitle: t('common:participants.listFilterOptions.adherence.title'),
    columnName: 'adherence',
    options: [
      { name: t('common:participants.listFilterOptions.adherence.options.all'), value: 'ALL' },
      { name: t('common:participants.listFilterOptions.adherence.options.25AndBelow'), value: '25_AND_BELOW' },
      { name: t('common:participants.listFilterOptions.adherence.options.251To50'), value: '25.1_50' },
      { name: t('common:participants.listFilterOptions.adherence.options.501To75'), value: '50.1_75' },
      { name: t('common:participants.listFilterOptions.adherence.options.751AndAbove'), value: '75.1_ABOVE' },
    ],
  },
  {
    columnTitle: t('common:participants.listFilterOptions.age.title'),
    columnName: 'age',
    options: [
      { name: t('common:participants.listFilterOptions.age.options.all'), value: 'ALL' },
      { name: t('common:participants.listFilterOptions.age.options.25AndBelow'), value: '25_AND_BELOW' },
      { name: t('common:participants.listFilterOptions.age.options.26To40'), value: '26_40' },
      { name: t('common:participants.listFilterOptions.age.options.41To60'), value: '41_60' },
      { name: t('common:participants.listFilterOptions.age.options.61To80'), value: '61_80' },
      { name: t('common:participants.listFilterOptions.age.options.81AndAbove'), value: '81_ABOVE' },
    ],
  },
  {
    columnTitle: t('common:participants.listFilterOptions.gender.title'),
    columnName: 'gender',
    options: [
      { name: t('common:participants.listFilterOptions.gender.options.all'), value: 'ALL' },
      { name: t('common:participants.listFilterOptions.gender.options.female'), value: 'FEMALE' },
      { name: t('common:participants.listFilterOptions.gender.options.male'), value: 'MALE' },
      { name: t('common:participants.listFilterOptions.gender.options.others'), value: 'OTHERS' },
      { name: t('common:participants.listFilterOptions.gender.options.unknown'), value: 'UNKNOWN' },
      { name: t('common:participants.listFilterOptions.gender.options.unspecified'), value: 'UNSPECIFIED' },
    ],
  },
  {
    columnTitle: t('common:participants.listFilterOptions.lastMeasurement.title'),
    columnName: 'lastMeasurement',
    options: [
      { name: t('common:participants.listFilterOptions.lastMeasurement.options.all'), value: 'ALL' },
      { name: t('common:participants.listFilterOptions.lastMeasurement.options.criticallyLow'), value: 'LL' },
      { name: t('common:participants.listFilterOptions.lastMeasurement.options.criticallyHigh'), value: 'HH' },
      { name: t('common:participants.listFilterOptions.lastMeasurement.options.normal'), value: 'N' },
      { name: t('common:participants.listFilterOptions.lastMeasurement.options.low'), value: 'L' },
      { name: t('common:participants.listFilterOptions.lastMeasurement.options.high'), value: 'H' },
    ],
  },
  {
    columnTitle: t('common:participants.listFilterOptions.onboarded.title'),
    columnName: 'onboarded',
    options: [
      { name: t('common:participants.listFilterOptions.onboarded.options.all'), value: 'ALL' },
      { name: t('common:participants.listFilterOptions.onboarded.options.yes'), value: 'YES' },
      { name: t('common:participants.listFilterOptions.onboarded.options.no'), value: 'NO' },
    ],
  },
  {
    columnTitle: t('common:participants.listFilterOptions.status.title'),
    columnName: 'enrolledProgramStatus',
    options: [
      { name: t('common:participants.listFilterOptions.status.options.all'), value: 'ALL' },
      { name: t('common:participants.listFilterOptions.status.options.enrolled'), value: 'ENROLLED' },
      // { name: 'Discharged', value: 'DISCHARGED' },
      { name: t('common:participants.listFilterOptions.status.options.notEnrolled'), value: 'NOT_ENROLLED' },
    ],
  },
];

export const alertListFilterOptions = (t, bodyCompositionEnabled) => {
  return [
    {
      columnTitle: t('common:alerts.alertTypes.title'),
      columnName: 'alertType',
      options: [
        { name: t('common:listFilterOptions.common.all'), value: 'ALL' },
        { name: t('common:alerts.alertTypes.criticallyLow'), value: 'CRITICALLY_LOW' },
        { name: t('common:alerts.alertTypes.criticallyHigh'), value: 'CRITICALLY_HIGH' },
        { name: t('common:alerts.alertTypes.missed'), value: 'MISSED' },
        { name: t('common:alerts.alertTypes.low'), value: 'LOW' },
        { name: t('common:alerts.alertTypes.high'), value: 'HIGH' },
      ],
    },
    {
      columnTitle: t('common:alerts.alertStatus.title'),
      columnName: 'alertStatus',
      options: [
        { name: t('common:listFilterOptions.common.all'), value: 'ALL' },
        { name: t('common:alerts.alertStatus.new'), value: 'new' },
        { name: t('common:alerts.alertStatus.reviewed'), value: 'REVIEWED' },
        { name: t('common:alerts.alertStatus.closed'), value: 'CLOSED' },
      ],
    },
    {
      columnTitle: t('common:vitals.vitalTypes.title'),
      columnName: 'vitalType',
      options: [
        { name: t('common:listFilterOptions.common.all'), value: 'ALL' },
        { name: t('common:vitals.vitalTypes.bg'), value: 'BG' },
        { name: t('common:vitals.vitalTypes.bp'), value: 'BP' },
        { name: t('common:vitals.vitalTypes.btm'), value: 'BTM' },
        { name: t('common:vitals.vitalTypes.hs'), value: 'HS' },
        { name: t('common:vitals.vitalTypes.hr'), value: 'HR' },
        { name: t('common:vitals.vitalTypes.rr'), value: 'RR' },
        { name: t('common:vitals.vitalTypes.po.002'), value: 'PO' },
        { name: t('common:vitals.vitalTypes.monit'), value: 'MONIT' },
        ...(bodyCompositionEnabled ? [{ name: t('common:vitals.vitalTypes.bc'), value: 'BC' }] : []),
      ],
    },
  ];
};

const chargeItemCodeFilterOptions = (t) => {
  return [
    {
      columnTitle: t('common:tags.chargeCode.title'),
      columnName: 'chargeCodeType',
      options: [
        { name: t('common:tags.all'), value: 'ALL' },
        { name: t('common:tags.chargeCode.base'), value: 'base' },
        { name: t('common:tags.chargeCode.addOnAfter'), value: 'addon_after' },
        { name: t('common:tags.chargeCode.addOnDuring'), value: 'addon_during' },
        { name: t('common:tags.chargeCode.consumables'), value: 'consumables' },
        { name: t('common:tags.chargeCode.kiosk'), value: 'kiosk' },
      ],
    },
    {
      columnTitle: t('common:tags.tax.title'),
      columnName: 'tax',
      options: [
        { name: t('common:tags.all'), value: 'ALL' },
        { name: t('common:tags.tax.exclusive'), value: 'exclusive' },
        { name: t('common:tags.tax.inclusive'), value: 'inclusive' },
      ],
    },
    {
      columnTitle: t('common:tags.suspensionExtension.title'),
      columnName: 'extensionSuspension',
      options: [
        { name: t('common:tags.all'), value: 'ALL' },
        { name: t('common:tags.suspensionExtension.suspensionExtension'), value: 'extension_suspension' },
        { name: t('common:tags.suspensionExtension.extension'), value: 'extension' },
        { name: t('common:tags.suspensionExtension.suspension'), value: 'suspension' },
        { name: t('common:tags.suspensionExtension.notApplicable'), value: 'not_applicable' },
      ],
    },
    {
      columnTitle: t('common:tags.sku.title'),
      columnName: 'sku',
      options: [
        { name: t('common:tags.all'), value: 'ALL' },
        { name: t('common:tags.sku.bg'), value: 'BG' },
        { name: t('common:tags.sku.bp'), value: 'BP' },
        { name: t('common:tags.sku.hs'), value: 'HS' },
        { name: t('common:tags.sku.sp02'), value: 'PO' },
        { name: t('common:tags.sku.btm'), value: 'BTM' },
        { name: t('common:tags.sku.4in1'), value: 'HILLROM_WACSM' },
        { name: t('common:tags.sku.5in1'), value: 'HILLROM_WACSM_RR' },
        { name: t('common:tags.sku.monit'), value: 'MONIT' },
        { name: t('common:tags.sku.ts'), value: 'TEST-STRIPS' },
        { name: t('common:tags.sku.cs'), value: 'CONTROL-SOLUTION' },
        { name: t('common:tags.sku.lance'), value: 'LANCE' },
      ],
    },
  ];
};

export const alertSummaryListFilterOptions = (t) => {
  return [
    {
      columnTitle: t('common:alerts.alertTypes.title'),
      columnName: 'alertType',
      options: [
        { name: t('common:listFilterOptions.common.all'), value: 'ALL' },
        { name: t('common:listFilterOptions.alertSummary.options.continuous'), value: 'CONTINUOUS' },
        { name: t('common:listFilterOptions.alertSummary.options.spot'), value: 'NON_CONTINUOUS' },
      ],
    },
  ];
};

export const pausedSummaryListFilterOptions = (t) => {
  return [
    {
      columnTitle: t('common:vitals.vitalTypes.title'),
      columnName: 'vitalType',
      options: [
        { name: t('common:listFilterOptions.common.all'), value: 'ALL' },
        { name: t('common:vitals.vitalTypes.hr'), value: 'HR' },
        { name: t('common:vitals.vitalTypes.rr'), value: 'RR' },
        { name: t('common:vitals.vitalTypes.po.002'), value: 'PO' },
      ],
    },
  ];
};

export const getBackgroundColorByGender = (gender) => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafari = ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1;

  if (gender === 'Male')
    return isSafari
      ? 'linear-gradient(rgba(255, 255, 255, 0), #48CAE4)'
      : 'linear-gradient(rgba(202, 240, 248),transparent)';
  else if (gender === 'Female')
    return isSafari
      ? 'linear-gradient(rgba(255, 255, 255, 0), #FFB5A7)'
      : 'linear-gradient(rgba(250, 225, 221),transparent)';
  return isSafari
    ? 'linear-gradient(rgba(255, 255, 255, 0), #9E9E9E)'
    : 'linear-gradient(rgba(224, 224, 224),transparent)';
};

export const getBackgroundByGender = (gender) => {
  if (gender === 'Male') return '#48CAE4';
  else if (gender === 'Female') return '#FFB5A7';
  return '#9E9E9E';
};

const validateQuery = (value, type) => {
  if (type == 'PAGE') {
    return parseInt(value) > 0;
  } else if (type == 'PAGE_SIZE') {
    return [10, 20, 50].includes(parseInt(value));
  }
};

const fetchImages = async (profile_photos = []) => {
  const images = [];
  for (const e of profile_photos) {
    const realImageUrl = await fetchGoThumbSignedUrl(e);
    images.push({
      avatar: e,
      realImageUrl,
    });
  }

  return images;
};

const months = [
  { key: 1, value: 1, label: 'January' },
  { key: 2, value: 2, label: 'February' },
  { key: 3, value: 3, label: 'March' },
  { key: 4, value: 4, label: 'April' },
  { key: 5, value: 5, label: 'May' },
  { key: 6, value: 6, label: 'June' },
  { key: 7, value: 7, label: 'July' },
  { key: 8, value: 8, label: 'August' },
  { key: 9, value: 9, label: 'September' },
  { key: 10, value: 10, label: 'October' },
  { key: 11, value: 11, label: 'November' },
  { key: 12, value: 12, label: 'December' },
];

const convertMonthFromNumToStr = (monthNum) => {
  switch (monthNum) {
    case 1:
      return 'January';
    case 2:
      return 'February';
    case 3:
      return 'March';
    case 4:
      return 'April';
    case 5:
      return 'May';
    case 6:
      return 'June';
    case 7:
      return 'July';
    case 8:
      return 'August';
    case 9:
      return 'September';
    case 10:
      return 'October';
    case 11:
      return 'November';
    case 12:
      return 'December';
  }
};

const formatOnsetDate = (year, month, t) => {
  if (!year && !month) return t('common:tags.status.notApplicable');

  if (year && !month) return String(year);

  if (year && month) {
    const monthKey = typeof month === 'number' ? month : month.value;

    const monthLabel = months.find((m) => m.key === monthKey).label;
    const monthShort = monthLabel.substring(0, 3);

    return `${monthShort}, ${year}`;
  }

  return '';
};

const normalizeLanguageLocale = (locale) => String(locale || 'en').toLowerCase().replace('_', '-');

const getLanguageCode = (locale) => normalizeLanguageLocale(locale).split('-')[0];

const dateFnslocaleMap = {
  en: enGB,
  vi: vi,
  zh: zhCN,
  th: th,
  id: id,
  ms: msLocale,
};

const getDateFnsLocaleMap = (locale) => {
  const normalizedLocale = getLanguageCode(locale);
  return (dateFnslocaleMap[normalizedLocale] && dateFnslocaleMap[normalizedLocale]) || dateFnslocaleMap.en;
};

const momentLocaleMap = {
  en: 'en',
  vi: 'vi',
  zh: 'zh-cn',
  th: 'th',
  id: 'id',
  ms: 'ms',
};

const getMomentLocaleMap = (locale) => {
  const normalizedLocale = getLanguageCode(locale);
  return (momentLocaleMap[normalizedLocale] && momentLocaleMap[normalizedLocale]) || momentLocaleMap.en;
};

const formatDateFnsLocale = (date, locale) => format(date || new Date(), 'dd MMM yyyy', { locale: getDateFnsLocaleMap(locale) });

const SCHEDULE_MAPPINGS = {
  FREQUENCY: {
    value: 'frequency',
    label: 'By Frequency',
  },
  TIMESLOT: {
    value: 'timeslots',
    label: 'By Timeslot',
  },
  TIMESLOT_MEAL: {
    value: 'meals',
    label: 'By Meals',
  },
  ONETIME: {
    value: 'date',
    label: 'By Date',
  },
};

const SCHEDULE_MAPPING = {
  FREQUENCY: {
    value: 'frequency',
    labelKey: 'schedule.byFrequency',
  },
  TIMESLOT: {
    value: 'timeslots',
    labelKey: 'schedule.byTimeslot',
  },
  TIMESLOT_MEAL: {
    value: 'meals',
    labelKey: 'schedule.byMeals',
  },
  ONETIME: {
    value: 'date',
    labelKey: 'schedule.byDate',
  },
};

export default {
  toTitleCase,
  maskEmail,
  truncateWithEllipsis,
  renameDocumentTitle,
  inputNumberLimit,
  listOptions,
  listSortedDuration,
  chargeItemCodeFilterOptions,
  participantListFilterOptions,
  alertListFilterOptions,
  alertSummaryListFilterOptions,
  pausedSummaryListFilterOptions,
  getBackgroundColorByGender,
  getBackgroundByGender,
  validateQuery,
  fetchImages,
  formatOnsetDate,
  months,
  convertMonthFromNumToStr,
  SCHEDULE_MAPPINGS,
  SCHEDULE_MAPPING,
  getLanguageCode,
  getDateFnsLocaleMap,
  getMomentLocaleMap,
  formatDateFnsLocale
};
